import { type MetricType } from 'features/charts-v2/types'
import {
  augmentMetricsForWeighting,
  METRIC_WEIGHT_BY,
} from 'features/charts-v2/utils/augment-metrics-for-weighting'

export { augmentMetricsForWeighting }

/**
 * A row's metric values in the positional order of the augmented metric list.
 * Index `i` is the value for `metricsForData[i]`; any hidden weight metric the
 * UI does not render still occupies its augmented index here.
 */
export type MetricValues = (number | null)[]

/**
 * Compute the totals map for a summary table footer / trend row.
 *
 * Count metrics (weight metric `null` in {@link METRIC_WEIGHT_BY}) are summed.
 * Average metrics are weighted by their grain's count metric, resolved by ID
 * against `metricsForData` — which is why callers MUST pass the AUGMENTED
 * positional list (see {@link augmentMetricsForWeighting}): the weight column
 * (e.g. sentence_count for sentiment) is often a hidden metric whose index lies
 * beyond the user's visible selection.
 *
 * Null/zero guards mirror the legacy per-block math: average metrics skip null
 * values and yield `null` when every weight in the group is zero/absent.
 *
 * @param rows - Each row's values in `metricsForData` positional order.
 * @param metricsForData - The augmented positional metric list.
 * @returns Map keyed `metric_${index}` for every index in `metricsForData`.
 *   Callers read only the indices they render.
 */
export function computeWeightedTotals(
  rows: MetricValues[],
  metricsForData: MetricType[],
): Record<string, number | null> {
  const totals: Record<string, number | null> = {}

  metricsForData.forEach((metric, index) => {
    const key = `metric_${index}`
    const weightMetric = METRIC_WEIGHT_BY[metric]

    if (weightMetric === null) {
      // Count metric: sum. Null when no row carried a value.
      let total = 0
      let hasData = false
      rows.forEach((values) => {
        const value = values[index]
        if (value != null) {
          total += value
          hasData = true
        }
      })
      totals[key] = hasData ? total : null
      return
    }

    // Average metric: weight by its grain's count metric.
    const weightIndex = metricsForData.indexOf(weightMetric)
    let weightedSum = 0
    let totalWeight = 0
    let hasNonNull = false

    rows.forEach((values) => {
      const value = values[index]
      if (value == null) return
      hasNonNull = true
      // Weight by the resolved count column; fall back to 1 when the weight
      // metric is absent from the augmented list (should not happen once the
      // augmentation guarantees it, but keeps a single missing column safe).
      const weight = weightIndex >= 0 ? (values[weightIndex] ?? 0) : 1
      weightedSum += value * weight
      totalWeight += weight
    })

    totals[key] =
      hasNonNull && totalWeight > 0 ? weightedSum / totalWeight : null
  })

  return totals
}
